import { Button, Col, Form, Input, message, Row, Select, Spin, Card, Radio, Space, Upload, Typography } from "antd";
import { ExclamationCircleOutlined, WarningOutlined, CheckCircleOutlined, UploadOutlined, PaperClipOutlined, DeleteOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import _NewapplicationService from "../../modules/NewapplicationService";
import { useEffect, useState, useRef } from "react";
import _BankerService from "../../modules/BankerService";
import { COMMON_ERROR_MSG } from "../../constants/constants";
import { useHistory } from "react-router-dom";
import { routes } from "../../routes/Routes";
import { PRIVATE_WEALTH_ADVISOR_ROLE, COMPLIANCE_ADMINISTRATOR_ROLE, UNDERWRITER_BANKER_ROLE, SENIOR_PRIVATE_BANKER_ROLE, applicationStatus } from "../../constants/DataConstants";
import StatusTag from "../../components/StatusTag";
import ButtonComponent from "../../components/Button/Button";
import IntegerInput from "../../components/IntergerInput";
import "./ConcludeLoanRequest.scss";

const { Option } = Select;
const { confirm } = Modal;
const { Text } = Typography;

const DOC_TYPE = "updoc3_credit_bureau_report_cbr";
const FIELD_KEY = "creditBureauReport";

const ConcludeLoanRequest = ({ applicantDetails, ApplicationObjId: ApplicationObjIdProp }) => {
    const [form] = Form.useForm();
    const cbrScoreValue = Form.useWatch('CBRScore_ComplienceAdmin', form);
    const selectedApplicationStatus = Form.useWatch('applicationStatus', form);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [uploadedPath, setUploadedPath] = useState("");
    const [amlKycFileList, setAmlKycFileList] = useState([]);
    const [amlKycFilePaths, setAmlKycFilePaths] = useState([]);
    const [isRecommendationLimitBeyond, setIsRecommendationLimitBeyond] = useState(false);
    const amlKycRowIdRef = useRef(1);
    const getNextAmlKycRowId = () => amlKycRowIdRef.current++;

    const history = useHistory();
    const ApplicationObjId = parseInt(ApplicationObjIdProp);
    const bankerIdRaw = localStorage.getItem("BankerId");
    const BankerId = bankerIdRaw ? parseInt(bankerIdRaw) : null;

    const complianceIdRaw = localStorage.getItem("BankerId");
    const ComplianceId = complianceIdRaw ? parseInt(complianceIdRaw) : null;

    const isLoanOfficer = JSON.parse(localStorage.getItem("IsLoanofficerFlag"));
    const statusID = parseInt(localStorage.getItem("applicationStatusViewOnly"));
    const RoleId = Number(localStorage.getItem("RoleId") ?? 0);
    const isPrivateWealthAdvisor = localStorage.getItem("userRole") === PRIVATE_WEALTH_ADVISOR_ROLE;
    const isComplianceAdministrator = localStorage.getItem("userRole") === COMPLIANCE_ADMINISTRATOR_ROLE;
    const isUnderwriterBanker = localStorage.getItem("userRole") === UNDERWRITER_BANKER_ROLE;
    const isSeniorPrivateBanker = localStorage.getItem("userRole") === SENIOR_PRIVATE_BANKER_ROLE

    // Radio "AML/KYC Started" uses value="3" (Application_Status_Id === 3 represents
    // AML/KYC Started). The AML/KYC Docs upload is only mandatory/visible once the Compliance
    // Admin has that radio checked in this form; it stays hidden before that.
    const isAmlKycStartedOrLater = selectedApplicationStatus === "3";

    useEffect(() => {
        if (isComplianceAdministrator) {
            getCBRDetails();
        }
    }, []);

    useEffect(() => {
        if (isUnderwriterBanker) {
            getIsRecomadationLimitBeyondtheLimitForUnderwriterBanker();
        }
    }, []);

    if (isPrivateWealthAdvisor) {
        return null;
    }

    const getIsRecomadationLimitBeyondtheLimitForUnderwriterBanker = async () => {
        try {
            const res = await _BankerService.isRecomadationLimitBeyondtheLimitForUnderwriterBanker(BankerId, ApplicationObjId);
            if (res?.data === true) {
                setIsRecommendationLimitBeyond(true);
            }
        } catch (error) {
            console.error("Error fetching recommendation limit check:", error);
        }
    };

    const getCBRDetails = async () => {
        setLoading(true);
        try {
            const res = await _NewapplicationService.getApplicantInformationByApplicationId(ApplicationObjId);
            if (res?.data) {
                const resObj = res.data;
                const cbrScore = resObj.CBRScore_ComplienceAdmin;
                const docPath = resObj.UpDoc3_Credit_Bureau_Report_CBR;

                form.setFieldsValue({
                    CBRScore_ComplienceAdmin: cbrScore,
                    [FIELD_KEY]: docPath,
                });

                if (docPath) {
                    const relativePath = docPath.startsWith('http') ? new URL(docPath).pathname : docPath;
                    setUploadedPath(relativePath);
                    form.setFieldsValue({ [FIELD_KEY]: relativePath });
                    setFileList([
                        {
                            uid: "-1",
                            name: "Credit Bureau Report",
                            status: "done",
                            url: docPath,
                        },
                    ]);
                }

                const amlKycDocs = resObj.UpDoc_AML_KYC_Docs;
                if (amlKycDocs) {
                    const paths = amlKycDocs.split(",").filter(Boolean);
                    const items = paths.map((p, index) => ({
                        uid: `aml-${index}`,
                        rowId: index + 1,
                        name: p.split("/").pop().split("?")[0] || `AML_KYC_Doc_${index + 1}`,
                        status: "done",
                        url: p,
                        path: p,
                    }));
                    setAmlKycFileList(items);
                    setAmlKycFilePaths(paths);
                    amlKycRowIdRef.current = paths.length + 1;
                }
            }
        } catch (err) {
            console.error("Error fetching CBR details:", err);
        } finally {
            setLoading(false);
        }
    };

    const fileFormatText = (
        <Text type="secondary" style={{ fontSize: 12, marginLeft: 10 }}>
            Format: pdf &nbsp;&nbsp; Size: Up to 25MB.
        </Text>
    );

    const validateFile = (file) => {
        const isValidType = ["application/pdf"].includes(file.type);
        if (!isValidType) {
            message.error("Only PDF files are allowed!");
            return false;
        }
        const isValidSize = file.size <= 25 * 1024 * 1024;
        if (!isValidSize) {
            message.error("File size must be up to 25MB!");
            return false;
        }
        return true;
    };

    const handleUpload = async (file) => {
        if (!validateFile(file)) return Upload.LIST_IGNORE;

        const formData = new FormData();
        formData.append("files[]", file);

        setUploading(true);
        try {
            const res = await _NewapplicationService.UploadPdfOrDocFile(
                formData,
                ApplicationObjId,
                DOC_TYPE
            );

            if (res?.data?.IsSucess && res?.data?.URL) {

                //changes for pathname
                const relativePath = new URL(res.data.URL).pathname;
                setUploadedPath(relativePath);
                form.setFieldsValue({ [FIELD_KEY]: relativePath });
                setFileList([
                    {
                        uid: "-1",
                        name: file.name,
                        status: "done",
                        url: res.data.URL,
                    },
                ]);
                message.success(res.data.Message || "File uploaded successfully");
            } else {
                message.error(res?.data?.Message || "File upload failed");
            }
        } catch (err) {
            console.error("Upload error:", err);
            message.error(COMMON_ERROR_MSG);
        } finally {
            setUploading(false);
        }

        return Upload.LIST_IGNORE;
    };

    const showDeleteConfirm = () => {
        confirm({
            title: "Are you sure you want to delete this Credit Bureau Report file?",
            icon: <ExclamationCircleOutlined />,
            okText: <span id="yesBtn">Yes</span>,
            cancelText: <span id="noBtn">No</span>,
            okType: "primary",
            onOk() {
                setFileList([]);
                setUploadedPath("");
                form.setFieldsValue({ [FIELD_KEY]: "" });
            },
        });
    };

    const handleAmlKycUpload = async (file) => {
        if (!validateFile(file)) return Upload.LIST_IGNORE;
        const rowId = getNextAmlKycRowId();
        const formData = new FormData();
        formData.append("files[]", file);
        setUploading(true);
        try {
            const res = await _NewapplicationService.UploadPdfOrDocFileRowId(formData, ApplicationObjId, "updocamlkycdocs", rowId);
            if (res?.data?.IsSucess && res?.data?.URL) {
                const relativePath = new URL(res.data.URL).pathname;
                const uploadedItem = { uid: `${rowId}`, rowId, name: file.name, status: "done", url: res.data.URL, path: relativePath };
                setAmlKycFileList(prev => [...prev, uploadedItem]);
                setAmlKycFilePaths(prev => {
                    const next = [...prev, relativePath];
                    form.setFieldsValue({ amlKycDocs: next });
                    form.validateFields(["amlKycDocs"]).catch(() => { });
                    return next;
                });
                message.success(res.data.Message || "File uploaded successfully");
            } else {
                message.error(res?.data?.Message || "File upload failed");
            }
        } catch (err) {
            console.error(err);
            message.error(COMMON_ERROR_MSG);
        } finally {
            setUploading(false);
        }
        return Upload.LIST_IGNORE;
    };

    const removeAmlKycFile = (file) => {
        const idx = amlKycFileList.findIndex(f => f.uid === file.uid);
        setAmlKycFileList(prev => prev.filter(f => f.uid !== file.uid));
        if (idx !== -1) {
            setAmlKycFilePaths(prev => {
                const next = prev.filter((_, i) => i !== idx);
                form.setFieldsValue({ amlKycDocs: next });
                form.validateFields(["amlKycDocs"]).catch(() => { });
                return next;
            });
        }
    };

    const onFinish = async (values) => {
        let reqObj = {
            Section: "common",
            ApplicationId: ApplicationObjId,
            // Remarks: values.remarks,
            Comments: values.comments,
            // LoanOfficer table is going away -- Loan Officer's identity is now Banker.Id too,
            // so route it into whichever review-comment column matches the active role.
            ReviewCommentsBy_BankerId: isLoanOfficer ? null : (BankerId ? BankerId : ComplianceId ? ComplianceId : null),
            ReviewCommentsBy_LoanOfficerId: isLoanOfficer ? BankerId : null,
            ApplicationStatusId: parseInt(values.applicationStatus),
            RoleId: RoleId || null,

        }
        setLoading(true);
        try {
            if (isComplianceAdministrator && (values.CBRScore_ComplienceAdmin || uploadedPath)) {
                const cbrReqObj = {
                    Id: ApplicationObjId,
                    ScreenName: "updatecbrandcbrdoc",
                    ApplicationId: applicantDetails?.ApplicationId,
                    CBRScore_ComplienceAdmin: values.CBRScore_ComplienceAdmin,
                    UpDoc3_Credit_Bureau_Report_CBR: uploadedPath,
                    UpDoc_AML_KYC_Docs: amlKycFilePaths.join(","),
                };
                const cbrRes = await _NewapplicationService.submitApplicantData(cbrReqObj);
                if (!cbrRes?.data?.IsSuccess) {
                    message.error(cbrRes?.data?.Message || COMMON_ERROR_MSG);
                }
            }

            const res = await _BankerService.addApplicationReviewComment(reqObj);
            if (res.data.IsSuccess) {
                setLoading(false);
                message.success(res.data.Message);
                if (isLoanOfficer) {
                    history.push(routes.loanOfficerDashboard.path);
                } else if (isComplianceAdministrator) {
                    history.push(routes.loanApplicationsForComplianceAdmin.path);
                } else {
                    history.push(routes.loanapplication.path);
                }

            } else {
                setLoading(false);
                message.error(res.data.Message ? res.data.Message : COMMON_ERROR_MSG);
            }
        } catch (err) {
            setLoading(false);
            console.log(err);
        }
        finally {
            setLoading(false);
        }

    }
    // console.log(applicantDetails.sponserModel["BankerName"], "applicantDetails")
    return (
        <>
            <Spin spinning={loading || uploading}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                >
                    <Card
                        className="conclude-loan-request-card mt10 card-section"
                        title={
                            <Row justify="space-between">

                                <Col
                                    className="gutter-row"
                                    xs={{ span: 24 }}
                                    sm={{ span: 24 }}
                                    md={{ span: 24 }}
                                    lg={{ span: 12 }}
                                    xl={{ span: 12 }}
                                    xxl={{ span: 12 }}
                                >
                                    {isComplianceAdministrator ? (
                                        <span className="adminappstatus">
                                            Applicant CBR Score : &nbsp;
                                            <strong>{applicantDetails && applicantDetails.CBRScore || '-'}</strong>
                                        </span>
                                    ) : (
                                        <Col xs={24} style={{ marginTop: "25px" }}>
                                            <h3 className="chartbox-title">Conclude Loan Request</h3>
                                        </Col>
                                    )}
                                </Col>




                                <Col
                                    className="gutter-row adminappstatusCOlumn"
                                    xs={{ span: 24 }}
                                    sm={{ span: 24 }}
                                    md={{ span: 24 }}
                                    lg={{ span: 12 }}
                                    xl={{ span: 12 }}
                                    xxl={{ span: 12 }}
                                >
                                    <span>Application Status:</span>
                                    <StatusTag statusId={applicantDetails?.Application_Status_Id} />
                                </Col>


                            </Row>

                        }
                    >
                        <div>
                            <Row gutter={24} className="w100">
                                {isComplianceAdministrator && (
                                    <>
                                        <Col xs={24}>
                                            <h3 className="chartbox-title">CBR Score & Credit Bureau Report</h3>
                                        </Col>

                                        <Col xs={24} sm={24} md={12} lg={12}>
                                            <Form.Item
                                                label="CBR Score"
                                                name="CBRScore_ComplienceAdmin"
                                                rules={[
                                                    { required: true, message: "CBR Score is required" },
                                                    {
                                                        validator(_, value) {
                                                            if (!value) return Promise.resolve();
                                                            const num = Number(value);
                                                            if (isNaN(num) || !Number.isInteger(num)) {
                                                                return Promise.reject("The CBR score must contain only numeric values.");
                                                            }
                                                            if (num < 300 || num > 850) {
                                                                return Promise.reject("CBR Score must be between 300 and 850");
                                                            }
                                                            return Promise.resolve();
                                                        },
                                                    },
                                                ]}
                                            >
                                                <IntegerInput placeholder="Enter CBR Score" maxLength={3} />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={24} sm={24} md={12} lg={12}>
                                            <Form.Item
                                                label="Credit Bureau Report"
                                                name={FIELD_KEY}
                                                rules={[{ required: true, message: "Credit Bureau Report is required" }]}
                                                valuePropName="file"
                                                getValueFromEvent={() => uploadedPath}
                                            >
                                                <Upload
                                                    accept=".pdf"
                                                    beforeUpload={handleUpload}
                                                    fileList={fileList}
                                                    maxCount={1}
                                                    onRemove={showDeleteConfirm}
                                                    customRequest={({ onSuccess }) => onSuccess("ok")}
                                                    showUploadList={true}
                                                >
                                                    {fileList.length === 0 && (
                                                        <div className="upload-placeholder">
                                                            <UploadOutlined />
                                                            <span> Upload File</span>
                                                        </div>
                                                    )}
                                                </Upload>
                                                {fileFormatText}
                                            </Form.Item>
                                        </Col>

                                        {applicantDetails?.Application_Status_Id !== 5 && (<Col xs={24} sm={24} md={12} lg={12}>
                                            <Form.Item
                                                label="AML/KYC Docs"
                                                name="amlKycDocs"
                                                // Only mandatory when "AML/KYC Completed" (value="6") is selected;
                                                // other statuses (e.g. Compliance Hold) only need a comment.
                                                required={selectedApplicationStatus === "6"}
                                                dependencies={["applicationStatus"]}
                                                rules={[
                                                    ({ getFieldValue }) => ({
                                                        validator: (_, value) =>
                                                            getFieldValue("applicationStatus") !== "6" || (value && value.length > 0)
                                                                ? Promise.resolve()
                                                                : Promise.reject(new Error("AML/KYC Docs is required")),
                                                    }),
                                                ]}
                                            >
                                                <Upload
                                                    accept=".pdf"
                                                    beforeUpload={handleAmlKycUpload}
                                                    showUploadList={false}
                                                    customRequest={({ onSuccess }) => onSuccess("ok")}
                                                >
                                                    <div className="upload-placeholder">
                                                        <UploadOutlined />
                                                        <span>Upload File</span>
                                                    </div>
                                                </Upload>
                                                {fileFormatText}
                                                {amlKycFileList.length > 0 && (
                                                    <div style={{ marginTop: 8 }}>
                                                        {amlKycFileList.map(file => (
                                                            <div
                                                                key={file.uid}
                                                                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", background: "#1a1a1a", borderRadius: 8, marginBottom: 4, border: "1px dashed rgba(244,244,245,0.15)" }}
                                                            >
                                                                <span style={{ color: "rgba(244,244,245,0.85)", display: "flex", alignItems: "center", gap: 6, overflow: "hidden" }}>
                                                                    <PaperClipOutlined />
                                                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                                                                </span>
                                                                <DeleteOutlined
                                                                    style={{ color: "#ff4d4f", cursor: "pointer", flexShrink: 0, marginLeft: 8 }}
                                                                    onClick={() => removeAmlKycFile(file)}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </Form.Item>
                                        </Col>
                                        )}
                                    </>
                                )}

                                {isComplianceAdministrator && (
                                    <Col xs={24} style={{ marginTop: "25px" }}>
                                        <h3 className="chartbox-title">Conclude Loan Request</h3>
                                    </Col>
                                )}

                                <Col
                                    className="gutter-row"
                                    xs={{ span: 24 }}
                                    sm={{ span: 24 }}
                                    md={{ span: 24 }}
                                    lg={{ span: 24 }}
                                    xl={{ span: 24 }}
                                    xxl={{ span: 24 }}
                                >
                                    <Form.Item
                                        // label="Application Status"
                                        name="applicationStatus"
                                        rules={[
                                            {
                                                required: true,
                                                message: "Application Status is required",
                                            },
                                        ]}
                                    >
                                        {isComplianceAdministrator ? (
                                            <Radio.Group className="line-height w100">
                                                <Space direction="vertical" className="w100">

                                                    <Radio
                                                        value="3"
                                                        className="w100 conclude-radio"
                                                        disabled={applicantDetails?.Application_Status_Id === 2 || applicantDetails?.Application_Status_Id === 3 || applicantDetails?.Application_Status_Id === 6 || applicantDetails?.Application_Status_Id === 7 || applicantDetails?.Application_Status_Id === 8 || applicantDetails?.Application_Status_Id === 9 || applicantDetails?.Application_Status_Id === 10 || applicantDetails?.Application_Status_Id === 11 || applicantDetails?.Application_Status_Id === 12 || applicantDetails?.Application_Status_Id === 13 || applicantDetails?.Application_Status_Id === 14 || applicantDetails?.Application_Status_Id === 15 || applicantDetails?.Application_Status_Id === 16}
                                                    >
                                                        <div className="conclude-radio-card">
                                                            <span className="conclude-radio-icon success"><CheckCircleOutlined className="successColor" /></span>
                                                            <div>
                                                                <div className="conclude-radio-title">
                                                                    AML/KYC Started

                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Radio>
                                                    <Radio
                                                        value="6"
                                                        className="w100 conclude-radio"
                                                        disabled={applicantDetails?.IsClear_Completed === true || applicantDetails?.Application_Status_Id === 2 || applicantDetails?.Application_Status_Id === 5 || applicantDetails?.Application_Status_Id === 6 || applicantDetails?.Application_Status_Id === 14 || applicantDetails?.Application_Status_Id === 16}
                                                    >
                                                        <div className="conclude-radio-card">
                                                            <span className="conclude-radio-icon success"><CheckCircleOutlined className="successColor" /></span>
                                                            <div>
                                                                <div className="conclude-radio-title">
                                                                    AML/KYC Completed

                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Radio>
                                                    <Radio
                                                        value="16"
                                                        className="w100 conclude-radio"
                                                        // disabled={applicantDetails?.IsClear_Completed === true || applicantDetails?.Application_Status_Id === 2 || applicantDetails?.Application_Status_Id === 5 || applicantDetails?.Application_Status_Id === 6 || applicantDetails?.Application_Status_Id === 14 || applicantDetails?.Application_Status_Id === 16}
                                                        disabled={(applicantDetails?.IsClear_Completed == true && applicantDetails?.IsApplicationApproved == true) || applicantDetails?.Application_Status_Id === 14 || applicantDetails?.Application_Status_Id === 16}

                                                    >
                                                        <div className="conclude-radio-card">
                                                            <span className="conclude-radio-icon success"><CheckCircleOutlined className="successColor" /></span>
                                                            <div>
                                                                <div className="conclude-radio-title">
                                                                    Compliance Hold

                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Radio>

                                                </Space>
                                            </Radio.Group>

                                        ) : isLoanOfficer ? (
                                            <Radio.Group className="line-height w100">
                                                <Space direction="vertical" className="w100">

                                                    <Radio
                                                        value="13"
                                                        className="w100 conclude-radio"
                                                        disabled={(applicantDetails?.IsClear_Completed == true && applicantDetails?.IsApplicationApproved == true) || applicantDetails?.Application_Status_Id === 13 || applicantDetails?.Application_Status_Id === 14 || applicantDetails?.Application_Status_Id === 16}
                                                    >
                                                        <div className="conclude-radio-card">
                                                            <span className="conclude-radio-icon success"><CheckCircleOutlined className="successColor" />
                                                            </span>
                                                            <div>
                                                                <div className="conclude-radio-title">
                                                                    Loan Application Approved
                                                                </div>
                                                                {/* <div className="conclude-radio-desc">
                                                                    Approve and fund this application
                                                                </div> */}
                                                            </div>
                                                        </div>
                                                    </Radio>

                                                    <Radio
                                                        value="14"
                                                        className="w100 conclude-radio"
                                                        disabled={(applicantDetails?.IsClear_Completed == true && applicantDetails?.IsApplicationApproved == true) || applicantDetails?.Application_Status_Id === 13 || applicantDetails?.Application_Status_Id === 14 || applicantDetails?.Application_Status_Id === 16}
                                                    >
                                                        <div className="conclude-radio-card">
                                                            <span className="conclude-radio-icon danger"><ExclamationCircleOutlined className="exceptionColor" /></span>
                                                            <div>
                                                                <div className="conclude-radio-title">
                                                                    Loan Application Rejected
                                                                </div>
                                                                {/* <div className="conclude-radio-desc">
                                                                    Decline this application
                                                                </div> */}
                                                            </div>
                                                        </div>
                                                    </Radio>

                                                    <Radio
                                                        value="7"
                                                        className="w100 conclude-radio"
                                                        disabled={(applicantDetails?.IsClear_Completed == true && applicantDetails?.IsApplicationApproved == true) || applicantDetails?.Application_Status_Id === 13 || applicantDetails?.Application_Status_Id === 14 || applicantDetails?.Application_Status_Id === 16}
                                                    >
                                                        <div className="conclude-radio-card">
                                                            <span className="conclude-radio-icon warning"><WarningOutlined className="warningIconColor" /></span>
                                                            <div>
                                                                <div className="conclude-radio-title">
                                                                    Action Required for Loan Application (Send to Banker)
                                                                </div>
                                                                {/* <div className="conclude-radio-desc">
                                                                    Request additional information from applicant
                                                                </div> */}
                                                            </div>
                                                        </div>
                                                    </Radio>

                                                </Space>
                                            </Radio.Group>
                                        ) : isUnderwriterBanker ? (
                                            <Radio.Group className="line-height w100">
                                                <Space direction="vertical" className="w100">

                                                    <Radio
                                                        value="15"
                                                        className="w100 conclude-radio"
                                                        disabled={(applicantDetails?.IsClear_Completed == true && applicantDetails?.IsApplicationApproved == true) || applicantDetails?.Application_Status_Id === 5 || applicantDetails?.Application_Status_Id === 7 || applicantDetails?.Application_Status_Id === 9 || applicantDetails?.Application_Status_Id === 12 || applicantDetails?.Application_Status_Id === 13 || applicantDetails?.Application_Status_Id === 14 || applicantDetails?.Application_Status_Id === 15 || applicantDetails?.Application_Status_Id === 11 || applicantDetails?.Application_Status_Id === 11 || applicantDetails?.Application_Status_Id === 16}
                                                    >
                                                        <div className="conclude-radio-card">
                                                            <span className="conclude-radio-icon success"><CheckCircleOutlined className="successColor" /></span>
                                                            <div>
                                                                <div className="conclude-radio-title">
                                                                    Propose
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Radio>

                                                    <Radio
                                                        value="11"
                                                        className="w100 conclude-radio"
                                                        disabled={(applicantDetails?.IsClear_Completed == true && applicantDetails?.IsApplicationApproved == true) || applicantDetails?.Application_Status_Id === 5 || applicantDetails?.Application_Status_Id === 7 || applicantDetails?.Application_Status_Id === 9 || applicantDetails?.Application_Status_Id === 12 || applicantDetails?.Application_Status_Id === 13 || applicantDetails?.Application_Status_Id === 14 || applicantDetails?.Application_Status_Id === 15 || applicantDetails?.Application_Status_Id === 11 || isRecommendationLimitBeyond || applicantDetails?.Application_Status_Id === 16}
                                                    >
                                                        <div className="conclude-radio-card">
                                                            <span className="conclude-radio-icon success"><CheckCircleOutlined className="successColor" /></span>
                                                            <div>
                                                                <div className="conclude-radio-title">
                                                                    Recommended For Approval (Send to Loan Officer)
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Radio>

                                                    <Radio
                                                        value="9"
                                                        className="w100 conclude-radio"
                                                        disabled={applicantDetails?.Application_Status_Id === 5 || applicantDetails?.Application_Status_Id === 7 || applicantDetails?.Application_Status_Id === 12 || applicantDetails?.Application_Status_Id === 13 || applicantDetails?.Application_Status_Id === 14 || applicantDetails?.Application_Status_Id === 15 || applicantDetails?.Application_Status_Id === 11 || applicantDetails?.Application_Status_Id === 9 || applicantDetails?.Application_Status_Id === 16}
                                                    >
                                                        <div className="conclude-radio-card">
                                                            <span className="conclude-radio-icon success"><CheckCircleOutlined className="successColor" /></span>
                                                            <div>
                                                                <div className="conclude-radio-title">
                                                                    Action Required for Loan Application (Send to Client)
                                                                </div>
                                                                {/* <div className="conclude-radio-desc">
                                                                    Send application to capital provider
                                                                </div> */}
                                                            </div>
                                                        </div>
                                                    </Radio>

                                                </Space>
                                            </Radio.Group>
                                        ) : (
                                            <Radio.Group className="line-height w100">
                                                <Space direction="vertical" className="w100">

                                                    <Radio
                                                        value="11"
                                                        className="w100 conclude-radio"
                                                        disabled={(applicantDetails?.IsClear_Completed == true && applicantDetails?.IsApplicationApproved == true) || applicantDetails?.Application_Status_Id === 3 || applicantDetails?.Application_Status_Id === 5 || applicantDetails?.Application_Status_Id == 8 || applicantDetails?.Application_Status_Id === 9 || applicantDetails?.Application_Status_Id === 12 || applicantDetails?.Application_Status_Id === 13 || applicantDetails?.Application_Status_Id === 14 || applicantDetails?.Application_Status_Id === 11 || applicantDetails?.Application_Status_Id === 16}
                                                    >
                                                        <div className="conclude-radio-card">
                                                            <span className="conclude-radio-icon success"><CheckCircleOutlined className="successColor" /></span>
                                                            <div>
                                                                <div className="conclude-radio-title">
                                                                    Recommended For Approval (Send to Loan Officer)
                                                                </div>
                                                                {/* <div className="conclude-radio-desc">
                                                                    Send application to capital provider
                                                                </div> */}
                                                            </div>
                                                        </div>
                                                    </Radio>


                                                    <Radio
                                                        value="9"
                                                        className="w100 conclude-radio"
                                                        disabled={(applicantDetails?.IsClear_Completed == true && applicantDetails?.IsApplicationApproved == true) || applicantDetails?.Application_Status_Id === 3 || applicantDetails?.Application_Status_Id === 5 || applicantDetails?.Application_Status_Id === 8 || applicantDetails?.Application_Status_Id === 12 || applicantDetails?.Application_Status_Id === 13 || applicantDetails?.Application_Status_Id === 14 || applicantDetails?.Application_Status_Id === 11 || applicantDetails?.Application_Status_Id === 9 || applicantDetails?.Application_Status_Id === 16}
                                                    >
                                                        <div className="conclude-radio-card">
                                                            <span className="conclude-radio-icon success"><CheckCircleOutlined className="successColor" /></span>
                                                            <div>
                                                                <div className="conclude-radio-title">
                                                                    Action Required for Loan Application (Send to Client)
                                                                </div>
                                                                {/* <div className="conclude-radio-desc">
                                                                    Send application to capital provider
                                                                </div> */}
                                                            </div>
                                                        </div>
                                                    </Radio>

                                                </Space>
                                            </Radio.Group>
                                        )}
                                    </Form.Item>

                                </Col>

                                {/* <Col
                                    className="gutter-row"
                                    xs={{ span: 24 }}
                                    sm={{ span: 24 }}
                                    md={{ span: 12 }}
                                    lg={{ span: 12 }}
                                    xl={{ span: 16 }}
                                    xxl={{ span: 16 }}
                                >
                                    <Form.Item label="Comments" name="comment"
                                        rules={[{ required: true, message: "Comment is required" }]}
                                    >
                                        <Input.TextArea
                                            autoSize={{ minRows: 3, maxRows: 5 }}
                                            placeholder="Comments"
                                            disabled={applicantDetails?.Application_Status_Id === 8 || applicantDetails?.Application_Status_Id === 9 || applicantDetails?.Application_Status_Id === 10}
                                        />
                                    </Form.Item>
                                </Col> */}

                                < Col
                                    className="gutter-row"
                                    xs={{ span: 24 }}
                                    sm={{ span: 24 }}
                                    md={{ span: 24 }}
                                    lg={{ span: 24 }}
                                    xl={{ span: 24 }}
                                    xxl={{ span: 24 }}
                                >
                                    <Form.Item
                                        label="Comments"
                                        name="comments"
                                        rules={[
                                            {
                                                required: true,
                                                message: "Comments are required"
                                            }
                                        ]}
                                    >
                                        <Input.TextArea
                                            autoSize={{ minRows: 3, maxRows: 5 }}
                                            placeholder="Comments"
                                            // disabled={applicantDetails?.Application_Status_Id === 13 || applicantDetails?.Application_Status_Id === 14}

                                            disabled={
                                                isComplianceAdministrator
                                                    ? (
                                                       (applicantDetails?.IsClear_Completed == true && applicantDetails?.IsApplicationApproved == true) ||
                                                        applicantDetails?.Application_Status_Id === 14 ||
                                                        applicantDetails?.Application_Status_Id === 16
                                                    )
                                                    : isSeniorPrivateBanker
                                                        ? (
                                                            applicantDetails?.Application_Status_Id === 3 ||
                                                            applicantDetails?.Application_Status_Id === 8 ||
                                                            applicantDetails?.Application_Status_Id == 11 ||
                                                            applicantDetails?.Application_Status_Id === 12 ||
                                                            applicantDetails?.Application_Status_Id == 13 ||
                                                            applicantDetails?.Application_Status_Id == 14 ||
                                                            applicantDetails?.Application_Status_Id == 16
                                                        )
                                                        : isLoanOfficer
                                                            ? (
                                                                (applicantDetails?.IsClear_Completed == true && applicantDetails?.IsApplicationApproved == true) ||
                                                                applicantDetails?.Application_Status_Id === 13 ||
                                                                applicantDetails?.Application_Status_Id === 14 ||
                                                                applicantDetails?.Application_Status_Id === 16
                                                            )
                                                            : (
                                                                applicantDetails?.Application_Status_Id === 7  ||
                                                                applicantDetails?.Application_Status_Id === 12 ||
                                                                applicantDetails?.Application_Status_Id === 13 ||
                                                                applicantDetails?.Application_Status_Id === 14 ||
                                                                applicantDetails?.Application_Status_Id === 15 ||
                                                                applicantDetails?.Application_Status_Id === 16
                                                            )
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row>
                                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                                    <ButtonComponent
                                        className="view-btn"
                                        text="Submit"
                                        htmlType="submit"
                                        disabled={
                                            isComplianceAdministrator
                                                ? (
                                                    (applicantDetails?.IsClear_Completed == true && applicantDetails?.IsApplicationApproved == true) ||
                                                    applicantDetails?.Application_Status_Id === 2 ||
                                                    applicantDetails?.Application_Status_Id === 14 ||
                                                    applicantDetails?.Application_Status_Id === 16
                                                )
                                                : isSeniorPrivateBanker
                                                    ? (
                                                        applicantDetails?.Application_Status_Id === 3  ||
                                                        applicantDetails?.Application_Status_Id === 8  ||
                                                        applicantDetails?.Application_Status_Id == 11  ||
                                                        applicantDetails?.Application_Status_Id === 12 ||
                                                        applicantDetails?.Application_Status_Id == 13  ||
                                                        applicantDetails?.Application_Status_Id == 14  ||
                                                        applicantDetails?.Application_Status_Id == 16
                                                    )
                                                    : isLoanOfficer
                                                        ? (
                                                            (applicantDetails?.IsClear_Completed == true && applicantDetails?.IsApplicationApproved == true) ||
                                                            applicantDetails?.Application_Status_Id === 13 ||
                                                            applicantDetails?.Application_Status_Id === 14 ||
                                                            applicantDetails?.Application_Status_Id === 16
                                                        )
                                                        : (
                                                            (applicantDetails?.IsClear_Completed == true && applicantDetails?.IsApplicationApproved == true) ||
                                                            applicantDetails?.Application_Status_Id === 7  ||
                                                            applicantDetails?.Application_Status_Id === 5  ||
                                                            applicantDetails?.Application_Status_Id === 12 ||
                                                            applicantDetails?.Application_Status_Id === 13 ||
                                                            applicantDetails?.Application_Status_Id === 14 ||
                                                            applicantDetails?.Application_Status_Id === 15 ||
                                                            applicantDetails?.Application_Status_Id === 11 ||
                                                            applicantDetails?.Application_Status_Id === 9  ||
                                                            applicantDetails?.Application_Status_Id === 16
                                                        )
                                        }
                                    />

                                </Col>

                                {/* <Col
                                    xs={24} sm={24} md={24} lg={24} xl={24}
                                    className="adminappstatus"

                                >
                                    <Space align="center">
                                        <strong>Application Status:</strong>
                                        <StatusTag statusId={applicantDetails?.Application_Status_Id} />
                                    </Space>
                                </Col> */}
                            </Row>
                        </div>
                    </Card>
                </Form>
            </Spin>
        </>
    )
}
export default ConcludeLoanRequest;
